import React from 'react';

import { Form } from 'antd';
import styled from 'styled-components';

import { FEES_PRICING_TYPES, FEES_TYPE_LABELS, FEES_TYPE_PRICING_TYPES } from 'constants/careTypes';

import ShopDataModalFeesEntryDropdown from './ShopDataModalFeesEntryDropdown';
import ShopDataModalFeesEntryNumeric from './ShopDataModalFeesEntryNumeric';
import { useContractContext } from '../../../common/Contract/ContractContext';
import CloseIcon from '../../../common/Icons/CloseIcon';
import { useAppSelector } from '../../../store/hook';
import { FeesDataType, FeeType } from '../../../types/fees';
import Input from '../../lib/Input';
import ShopDataModalAddEntryPlus from '../common/ShopDataModalAddEntryPlus';
import ShopDataModalCategoryHeader from '../common/ShopDataModalCategoryHeader';
import {
  FEES_LEVELS_FIELDS,
  FEES_LEVELS_FIELDS_KEYS,
  FIELD_CATEGORY,
  LEVEL_FEES_FIELDS,
  LEVEL_FEES_FIELDS_LABELS,
} from '../constants';
import { useShopDataModalContext } from '../ShopDataModalContext';
import { testIdFormattedLabel } from '../testIdUtils';

const StyledCloseIcon = styled.div`
  display: flex;
  visibility: hidden;
  border-radius: 50%;
  cursor: pointer;
  align-items: center;
  margin-top: 9px;
`;

const FeeTypeRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px 20px 30px 20px;

  &:hover {
    ${StyledCloseIcon} {
      visibility: visible;
    }
  }
`;

interface Props {
  type: FeeType;
  onChange: () => void;
}

const ShopDataModalLevelFees = ({ type, onChange }: Props) => {
  const feePricingTypes = useAppSelector((state) => state.global.feePricingTypes);

  const { currentLivingType, handleDataStatusChanged, setCurrentData } = useShopDataModalContext();
  const { selectedKey } = useContractContext();

  const pricingTypeOptions =
    feePricingTypes
      ?.filter((item: FeesDataType) => (FEES_TYPE_PRICING_TYPES[type] ?? []).includes(item.id))
      .map((item: FeesDataType) => ({ label: item.type, value: item.id })) ?? [];

  const handleAddFeeType = (item: string) => {
    if (!selectedKey || !currentLivingType?.abv) {
      return;
    }

    const livingTypeKey = currentLivingType.abv;

    setCurrentData((prevState: any) => {
      const prevFeesCategory = prevState[FIELD_CATEGORY.FEES] ?? {};
      const prevLivingTypeFees = prevFeesCategory[livingTypeKey] ?? {};
      const prevContractFees = prevLivingTypeFees[selectedKey as string] ?? {};
      const prevFeeTypeData = prevContractFees[type] ?? {};
      const updatedFields = [...(prevFeeTypeData?.fields ?? []), item].sort();

      return {
        ...prevState,
        [FIELD_CATEGORY.FEES]: {
          ...prevFeesCategory,
          [livingTypeKey]: {
            ...prevLivingTypeFees,
            [selectedKey as string]: {
              ...prevContractFees,
              [type]: {
                ...prevFeeTypeData,
                fee_type: type,
                living_type_id: currentLivingType?.id,
                fields: updatedFields,
              },
            },
          },
        },
      };
    });
    handleDataStatusChanged([FIELD_CATEGORY.FEES]);
    onChange();
  };

  const handleRemoveFeeType = (item: string) => {
    if (!selectedKey || !currentLivingType?.abv) {
      return;
    }

    handleDataStatusChanged([FIELD_CATEGORY.FEES]);

    const removalFields =
      item === LEVEL_FEES_FIELDS.FEES_PRICING_TYPE
        ? [item, LEVEL_FEES_FIELDS.FEE_NOTE, LEVEL_FEES_FIELDS.AVERAGE_COST, ...Object.values(FEES_LEVELS_FIELDS)]
        : [item];

    const livingTypeKey = currentLivingType?.abv;
    setCurrentData((prevState: any) => {
      const prevFeesCategory = prevState[FIELD_CATEGORY.FEES] ?? {};
      const prevLivingTypeFees = prevFeesCategory[livingTypeKey] ?? {};
      const prevContractFees = prevLivingTypeFees[selectedKey as string] ?? {};
      const prevFeeTypeData = prevContractFees[type] ?? {};
      const filteredFields = (prevFeeTypeData?.fields ?? []).filter((field: string) => !removalFields.includes(field));

      return {
        ...prevState,
        [FIELD_CATEGORY.FEES]: {
          ...prevFeesCategory,
          [livingTypeKey]: {
            ...prevLivingTypeFees,
            [selectedKey as string]: {
              ...prevContractFees,
              [type]: {
                ...prevFeeTypeData,
                ...Object.fromEntries(removalFields.map((field) => [field, null])),
                fields: filteredFields,
              },
            },
          },
        },
      };
    });
    onChange();
  };

  return (
    <>
      <ShopDataModalCategoryHeader label={FEES_TYPE_LABELS[type]} />
      <Container>
        <FeeTypeRow>
          <ShopDataModalFeesEntryDropdown
            key={LEVEL_FEES_FIELDS.FEES_PRICING_TYPE}
            category={FIELD_CATEGORY.FEES}
            field={LEVEL_FEES_FIELDS.FEES_PRICING_TYPE}
            name={[FIELD_CATEGORY.FEES, type, LEVEL_FEES_FIELDS.FEES_PRICING_TYPE]}
            options={pricingTypeOptions}
            dataTestId={`sd-modal-${testIdFormattedLabel(FEES_TYPE_LABELS[type])}-type-avg-input`}
          />
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const feesType = getFieldValue([FIELD_CATEGORY.FEES, type, LEVEL_FEES_FIELDS.FEES_PRICING_TYPE]);
              const averageCare = getFieldValue([FIELD_CATEGORY.FEES, type, LEVEL_FEES_FIELDS.AVERAGE_COST]);
              const hasPositiveLevel = FEES_LEVELS_FIELDS_KEYS.some(
                (levelField: string) => Number(getFieldValue([FIELD_CATEGORY.FEES, type, levelField])) > 0
              );
              const noteRequired =
                feesType === FEES_PRICING_TYPES.LEVELS && Number(averageCare) > 0 && !hasPositiveLevel;

              return (
                <Form.Item
                  style={{ flexGrow: 1 }}
                  name={[FIELD_CATEGORY.FEES, type, LEVEL_FEES_FIELDS.FEE_NOTE]}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!noteRequired || String(value ?? '').trim().length > 0) {
                          return Promise.resolve();
                        }

                        return Promise.reject('');
                      },
                    },
                  ]}
                >
                  <Input
                    style={{ height: 36 }}
                    placeholder={noteRequired ? 'Note' : 'Note (optional)'}
                    data-test-id={`sd-modal-${testIdFormattedLabel(FEES_TYPE_LABELS[type])}-note`}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
          <StyledCloseIcon onClick={() => handleRemoveFeeType(LEVEL_FEES_FIELDS.FEES_PRICING_TYPE)}>
            <CloseIcon size={20} />
          </StyledCloseIcon>
        </FeeTypeRow>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const feesType = getFieldValue([FIELD_CATEGORY.FEES, type, LEVEL_FEES_FIELDS.FEES_PRICING_TYPE]);
            const showAverageCareCostField = [
              FEES_PRICING_TYPES.LEVELS,
              FEES_PRICING_TYPES.POINTS,
              FEES_PRICING_TYPES.A_LA_CARTE,
            ].includes(feesType);
            const showLevelFields = feesType === FEES_PRICING_TYPES.LEVELS;
            const requiredAverageCareCost = feesType !== FEES_PRICING_TYPES.LEVELS && showAverageCareCostField;
            const populatedFields = getFieldValue([FIELD_CATEGORY.FEES, type, 'fields']) ?? [];
            const averageCare = getFieldValue([FIELD_CATEGORY.FEES, type, LEVEL_FEES_FIELDS.AVERAGE_COST]);
            const careNote = getFieldValue([FIELD_CATEGORY.FEES, type, LEVEL_FEES_FIELDS.FEE_NOTE]);
            const hasAverageCareWithNote = Number(averageCare) > 0 && String(careNote ?? '').trim().length > 0;
            const unpopulatedFields = FEES_LEVELS_FIELDS_KEYS.filter((item: string) => !populatedFields.includes(item));
            const fieldsOptions = unpopulatedFields.map((item: string) => ({
              key: item,
              label: LEVEL_FEES_FIELDS_LABELS[item],
              onClick: () => handleAddFeeType(item),
            }));

            return (
              <>
                <Form.Item noStyle name={[FIELD_CATEGORY.FEES, type, 'id']} />
                <Form.Item noStyle name={[FIELD_CATEGORY.FEES, type, 'living_type_id']} />
                {showAverageCareCostField && (
                  <ShopDataModalFeesEntryNumeric
                    key={LEVEL_FEES_FIELDS.AVERAGE_COST}
                    category={FIELD_CATEGORY.FEES}
                    field={LEVEL_FEES_FIELDS.AVERAGE_COST}
                    name={[FIELD_CATEGORY.FEES, type, LEVEL_FEES_FIELDS.AVERAGE_COST]}
                    required={requiredAverageCareCost}
                    rules={
                      requiredAverageCareCost
                        ? [
                            {
                              validator: (_, value) => {
                                if (Number.isFinite(value) && value <= 0) {
                                  return Promise.reject('Value must be greater than 0');
                                }

                                return Promise.resolve();
                              },
                            },
                          ]
                        : undefined
                    }
                    onRemove={handleRemoveFeeType}
                    dataTestId={`sd-modal-${testIdFormattedLabel(FEES_TYPE_LABELS[type])}-type-avg-input`}
                  />
                )}
                {showLevelFields && (
                  <>
                    {populatedFields.map((field: string) => {
                      return (
                        <ShopDataModalFeesEntryNumeric
                          key={field}
                          category={FIELD_CATEGORY.FEES}
                          field={field}
                          name={[FIELD_CATEGORY.FEES, type, field]}
                          required={!hasAverageCareWithNote}
                          rules={[
                            {
                              validator: () => {
                                if (hasAverageCareWithNote) {
                                  return Promise.resolve();
                                }

                                const hasPositiveLevel = populatedFields.some(
                                  (levelField: string) =>
                                    Number(getFieldValue([FIELD_CATEGORY.FEES, type, levelField])) > 0
                                );

                                return hasPositiveLevel
                                  ? Promise.resolve()
                                  : Promise.reject('');
                              },
                            },
                          ]}
                          onRemove={handleRemoveFeeType}
                        />
                      );
                    })}
                    {Boolean(unpopulatedFields?.length) && (
                      <ShopDataModalAddEntryPlus label={'Add Level'} options={fieldsOptions} />
                    )}
                  </>
                )}
              </>
            );
          }}
        </Form.Item>
      </Container>
    </>
  );
};

export default ShopDataModalLevelFees;
